import React from 'react';
import styled from 'styled-components';
import { FaDice, FaGem, FaClipboardList, FaExclamationTriangle } from 'react-icons/fa';
import StructuredData from '../components/SEO/StructuredData';
import AppStoreBadges from '../components/AppStoreBadges';

const Container = styled.div`
  min-height: 100vh;
  padding: 2rem 1rem;
  max-width: 800px;
  margin: 0 auto;
  color: var(--text-color);

  @media (min-width: 768px) {
    padding: 3rem 2rem;
  }
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--text-color);
  margin-bottom: 2rem;
  text-align: center;
  background: linear-gradient(135deg, var(--color-primary), var(--secondary-color));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  padding: 0.5rem 0;
`;

const Section = styled.section`
  margin-bottom: 3rem;
  animation: fadeIn 0.5s ease-out;

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-color);
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;

  svg {
    color: var(--color-primary);
  }
`;

const List = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 1.5rem 0;
`;

const ListItem = styled.li`
  margin-bottom: 1rem;
  padding-left: 1.5rem;
  position: relative;
  color: var(--text-color);
  line-height: 1.6;

  &::before {
    content: "•";
    color: var(--color-primary);
    position: absolute;
    left: 0;
    font-weight: bold;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin: 1.5rem 0;
  background: var(--card-background);
  border-radius: 8px;
  overflow: hidden;
  box-shadow: var(--shadow-elevation-low);
`;

const Th = styled.th`
  background: var(--color-primary);
  color: white;
  padding: 1rem;
  text-align: left;
  font-weight: 600;
`;

const Td = styled.td`
  padding: 1rem;
  border-top: 1px solid var(--border-color);
  color: var(--text-color);
`;

const Tr = styled.tr`
  &:nth-child(even) {
    background: var(--color-background-light);
  }

  &:hover {
    background: var(--color-primary-light);
  }
`;

const ImportantNote = styled.div`
  background: var(--color-warning-light);
  border-left: 4px solid var(--color-warning);
  padding: 1rem;
  margin: 1.5rem 0;
  border-radius: 4px;
  display: flex;
  align-items: flex-start;
  gap: 1rem;

  svg {
    color: var(--color-warning);
    flex-shrink: 0;
    margin-top: 0.25rem;
  }
`;

const CTASection = styled.section`
  margin-top: 4rem;
  padding: 3rem 2rem;
  background: linear-gradient(135deg, var(--color-primary) 0%, #ff8e8e 100%);
  border-radius: 20px;
  text-align: center;
  color: white;
`;

const CTATitle = styled.h2`
  font-size: 1.75rem;
  font-weight: 700;
  margin-bottom: 0.75rem;
`;

const CTAText = styled.p`
  font-size: 1.1rem;
  opacity: 0.95;
  margin-bottom: 1.5rem;
`;

const RulesPage: React.FC = () => {
  return (
    <>
      <StructuredData type="howto" path="/rules" />
      <Container>
        <Title>Pasur Rules</Title>

        <Section>
          <SectionTitle>
            <FaDice /> Basic Game Rules
          </SectionTitle>
          <List>
            <ListItem>Pasur is a traditional card game played with a standard 52-card deck.</ListItem>
            <ListItem>The game can be played with 2-4 players.</ListItem>
            <ListItem>Each player is dealt 4 cards at the beginning of each round.</ListItem>
            <ListItem>4 cards are also dealt face-up on the table.</ListItem>
            <ListItem>Players take turns playing one card from their hand.</ListItem>
            <ListItem>Players can either capture cards from the table or place a card on the table.</ListItem>
            <ListItem>The game continues until all cards have been played and captured.</ListItem>
          </List>
        </Section>

        <Section>
          <SectionTitle>
            <FaGem /> Scoring System
          </SectionTitle>
          <Table>
            <thead>
              <Tr>
                <Th>Achievement</Th>
                <Th>Points</Th>
                <Th>Description</Th>
              </Tr>
            </thead>
            <tbody>
              <Tr>
                <Td>Sur</Td>
                <Td>5</Td>
                <Td>Clearing all cards from the table</Td>
              </Tr>
              <Tr>
                <Td>Most Clubs</Td>
                <Td>7</Td>
                <Td>Having the most club cards</Td>
              </Tr>
              <Tr>
                <Td>10 of Diamonds</Td>
                <Td>3</Td>
                <Td>Capturing the 10 of Diamonds</Td>
              </Tr>
              <Tr>
                <Td>2 of Clubs</Td>
                <Td>2</Td>
                <Td>Capturing the 2 of Clubs</Td>
              </Tr>
              <Tr>
                <Td>Each Ace</Td>
                <Td>1</Td>
                <Td>Capturing any Ace</Td>
              </Tr>
              <Tr>
                <Td>Each Jack</Td>
                <Td>1</Td>
                <Td>Capturing any Jack</Td>
              </Tr>
            </tbody>
          </Table>
        </Section>

        <Section>
          <SectionTitle>
            <FaClipboardList /> Card Combinations
          </SectionTitle>
          <List>
            <ListItem>Cards A through 10 can capture table cards that sum to 11 (e.g., 7 captures 4, or captures 2+2).</ListItem>
            <ListItem>Jacks sweep all cards from the table except Kings and Queens.</ListItem>
            <ListItem>Kings and Queens can only capture cards of the same rank.</ListItem>
            <ListItem>If you can't capture, you must place a card on the table.</ListItem>
          </List>

          <ImportantNote>
            <FaExclamationTriangle />
            <div>
              A Sur (sweep) is only valid if: you don't use a Jack, it's not the final round,
              and you don't already have 50+ points. The first 62 points wins the game!
            </div>
          </ImportantNote>
        </Section>

        <CTASection>
          <CTATitle>Ready to Play?</CTATitle>
          <CTAText>
            Download the app and let us handle all the scoring for you!
          </CTAText>
          <AppStoreBadges variant="light" size="md" />
        </CTASection>
      </Container>
    </>
  );
};

export default RulesPage;
